import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';
import { getFeatureFlagSubject } from '../Api/useFeatureFlags';
import { addError } from './Error';
import { fetchInventory, getInventoryLoadingSubject, getInventorySubject } from './Inventory';
import { clearPriceBenchmarks, fetchPriceBenchmarks } from './PriceBenchMark';
import { fetchProjects, getProjectLoadingSubject, getProjectSubject } from './Project';
import { fetchCases, getCaseLoadingSubject, getCaseSubject } from './SupportCase';
import { fetchTags, getTagLoadingSubject, getTagSubject } from './Tags';
import { LinkedAccountOutcome, setAccountByUrl } from './User';
import { fetchWorkflowOrders, getWorkflowOrdersSubject } from './WorkflowOrder';

type ScopedStore = {
  clear: () => void;
  reload: (force: boolean) => Promise<void>;
  // Set where a read already running would either swallow the forced one or answer after it.
  running?: BehaviorSubject<boolean>;
};

// Each store keeps whatever it last loaded until forced, so a change of account scope empties and reloads it.
const scopedStores = (): ScopedStore[] => [
  { clear: () => getInventorySubject().next([]), reload: fetchInventory, running: getInventoryLoadingSubject() },
  // No running guard: its forced read queues behind any read in flight, and that read drops an old scope's answer.
  { clear: () => getWorkflowOrdersSubject().next([]), reload: fetchWorkflowOrders },
  { clear: () => getCaseSubject().next([]), reload: fetchCases, running: getCaseLoadingSubject() },
  { clear: () => getProjectSubject().next([]), reload: fetchProjects, running: getProjectLoadingSubject() },
  { clear: () => getTagSubject().next([]), reload: fetchTags, running: getTagLoadingSubject() },
  // Its forced read aborts the one in flight, whose answer is then discarded.
  ...(getFeatureFlagSubject().getValue().price_benchmarking
    ? [{ clear: clearPriceBenchmarks, reload: fetchPriceBenchmarks }]
    : [])
];

// A read begun under the old scope lands after the switch, so it is waited out and its answer cleared again.
const reloadStore = async ({ clear, reload, running }: ScopedStore): Promise<void> => {
  if (running) {
    await firstValueFrom(running.pipe(filter((busy) => !busy)));
    clear();
  }
  await reload(true);
};

// Emptied before the page a switch leads to mounts, so none renders the previous account's records meanwhile.
export const refreshAccountScopedData = (): void => {
  const stores = scopedStores();
  stores.forEach(({ clear }) => clear());
  // Started, never awaited: a read that does not settle would otherwise hold open a switch whose scope changed.
  Promise.all(stores.map(reloadStore)).catch((e) => addError('account', e instanceof Error ? e.message : String(e)));
};

// Resolves once the scope is switched; the reload carries on behind the page the link opens.
export const followAccountLink = async (accountId?: string): Promise<LinkedAccountOutcome> => {
  const outcome = await setAccountByUrl(accountId);
  if (outcome === 'switched') refreshAccountScopedData();
  return outcome;
};
